import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Send,
  Loader2,
  MessageCircle,
  Sparkles,
  Trophy,
  HelpCircle,
  BookOpen,
  Heart,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const KINDS = [
  { key: "all", label: "All", Icon: MessageCircle },
  { key: "story", label: "Stories", Icon: BookOpen },
  { key: "question", label: "Questions", Icon: HelpCircle },
  { key: "challenge", label: "Challenges", Icon: Trophy },
];

const KIND_META = {
  story: { color: "text-emerald-300", border: "border-emerald-400/30", Icon: BookOpen },
  question: { color: "text-cyan-300", border: "border-cyan-400/30", Icon: HelpCircle },
  challenge: { color: "text-amber-300", border: "border-amber-400/30", Icon: Trophy },
};

const REL = (ts) => {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

export default function Community() {
  const [kind, setKind] = useState("all");
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [cheeredIds, setCheeredIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("silentscan_cheers_v1") || "[]"));
    } catch {
      return new Set();
    }
  });

  const loadFeed = async (k = kind) => {
    setLoading(true);
    try {
      const params = k && k !== "all" ? { kind: k } : {};
      const { data } = await axios.get(`${API}/api/community/feed`, { params });
      setFeed(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeed(kind);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const cheer = async (id) => {
    if (cheeredIds.has(id)) return;
    try {
      const { data } = await axios.post(`${API}/api/community/cheer`, {
        post_id: id,
      });
      setFeed((f) =>
        f.map((p) => (p.id === id ? { ...p, cheers: data.cheers } : p))
      );
      const next = new Set(cheeredIds);
      next.add(id);
      setCheeredIds(next);
      try {
        localStorage.setItem(
          "silentscan_cheers_v1",
          JSON.stringify(Array.from(next))
        );
      } catch (e) {
        // storage full
      }
    } catch (e) {
      // network error
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(52,211,153,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(251,191,36,0.14) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="community-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <ShieldCheck className="w-3.5 h-3.5 text-cyan-300" />
            Anonymous · Supportive
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-12">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-emerald-300/80">
            Chapter 13 · Community
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            You&apos;re{" "}
            <span className="italic text-emerald-300">not alone.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            Share a win. Ask a question. Kick off a 7-day habit challenge. Every
            post here is anonymous by default.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2 border-b border-white/5 overflow-x-auto no-scrollbar w-full sm:w-auto min-w-0">
            {KINDS.map((k) => (
              <button
                key={k.key}
                data-testid={`community-tab-${k.key}`}
                onClick={() => setKind(k.key)}
                className={`px-4 py-2 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap ${
                  kind === k.key
                    ? "border-emerald-400 text-white"
                    : "border-transparent text-zinc-400 hover:text-white"
                }`}
              >
                <k.Icon className="w-4 h-4" />
                {k.label}
              </button>
            ))}
          </div>
          <button
            data-testid="community-compose"
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-400 text-black font-medium px-4 py-2 text-sm hover:bg-emerald-300"
          >
            <Plus className="w-4 h-4" />
            Share something
          </button>
        </div>

        <div className="mt-8 flex flex-col gap-3">
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading feed…
            </div>
          ) : feed.length === 0 ? (
            <div
              data-testid="community-empty"
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center"
            >
              <MessageCircle className="w-8 h-8 text-emerald-300 mx-auto" />
              <div className="mt-3 font-display text-lg font-semibold">
                Be the first to share
              </div>
              <p className="mt-1 text-sm text-zinc-400 max-w-md mx-auto">
                Post a small win, a question, or kick off a challenge.
              </p>
            </div>
          ) : (
            feed.map((p, i) => {
              const meta = KIND_META[p.kind] || KIND_META.story;
              const hasCheered = cheeredIds.has(p.id);
              return (
                <motion.div
                  key={p.id}
                  data-testid={`community-post-${p.id}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className={`rounded-3xl border ${meta.border} bg-white/[0.03] p-5`}
                >
                  <div className="flex items-center justify-between text-xs">
                    <div className={`inline-flex items-center gap-1.5 ${meta.color} font-semibold uppercase tracking-widest text-[10px]`}>
                      <meta.Icon className="w-3.5 h-3.5" />
                      {p.kind}
                    </div>
                    <div className="text-zinc-500">{REL(p.created_at)}</div>
                  </div>
                  <div className="mt-2 text-sm text-white leading-relaxed whitespace-pre-wrap">
                    {p.body}
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {p.tags?.map((t) => (
                        <span
                          key={t}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-400"
                        >
                          #{t}
                        </span>
                      ))}
                    </div>
                    <button
                      data-testid={`community-cheer-${p.id}`}
                      onClick={() => cheer(p.id)}
                      disabled={hasCheered}
                      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        hasCheered
                          ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                          : "border-white/10 bg-white/5 hover:bg-white/10 text-white"
                      }`}
                    >
                      <Heart className={`w-3.5 h-3.5 ${hasCheered ? "fill-current" : ""}`} />
                      {p.cheers} {p.cheers === 1 ? "cheer" : "cheers"}
                    </button>
                  </div>
                  <div className="mt-2 text-[11px] text-zinc-500">
                    — {p.name}
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>

      <AnimatePresence>
        {composing && (
          <ComposeModal
            onClose={() => setComposing(false)}
            onCreated={() => {
              setComposing(false);
              loadFeed(kind);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

const ComposeModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState("Anonymous");
  const [kind, setKind] = useState("story");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/community/post`, {
        name,
        kind,
        body: body.trim(),
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      });
      toast.success("Posted! ✨");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Post failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center p-3 sm:p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.3 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="community-compose-modal"
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0A0A14] p-6"
      >
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Share something
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {["story", "question", "challenge"].map((k) => (
            <button
              key={k}
              data-testid={`community-kind-${k}`}
              onClick={() => setKind(k)}
              className={`rounded-lg border px-3 py-2 text-xs uppercase tracking-widest font-semibold ${
                kind === k
                  ? "border-emerald-400 bg-emerald-400/10 text-white"
                  : "border-white/10 bg-white/5 text-zinc-300"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
            Display name (optional)
          </div>
          <input
            data-testid="community-compose-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          />
        </div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
            Your post
          </div>
          <textarea
            data-testid="community-compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            placeholder="Share a win, ask a question, or start a challenge…"
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400 resize-none"
          />
        </div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">
            Tags (comma separated, optional)
          </div>
          <input
            data-testid="community-compose-tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="cardio, sleep, habit"
            className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-white focus:outline-none focus:border-cyan-400"
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            data-testid="community-compose-cancel"
            onClick={onClose}
            className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-white px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            data-testid="community-compose-submit"
            onClick={submit}
            disabled={busy || !body.trim()}
            className="rounded-full bg-emerald-400 text-black font-medium px-5 py-2 text-sm hover:bg-emerald-300 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Post
          </button>
        </div>
        <div className="mt-3 text-[11px] text-zinc-500 flex items-start gap-2">
          <Sparkles className="w-3 h-3 mt-0.5 text-cyan-300" />
          Please be kind and share responsibly. Posts about self-harm are routed
          to helplines automatically.
        </div>
      </motion.div>
    </motion.div>
  );
};
